import { createContext, useContext, useReducer, useEffect } from 'react';
import type { ReactNode } from 'react';
import type { AppState, AppAction } from '../types';
import { persistenceService } from '../services/persistenceService';

interface AppContextType {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
}

const AppContext = createContext<AppContextType | null>(null);

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within AppProvider');
  }
  return context;
};

const appReducer = (state: AppState, action: AppAction): AppState => {
  switch (action.type) {
    case 'SET_CONTENT':
      return {
        ...state,
        document: {
          ...state.document,
          content: action.payload,
          lastModified: new Date(),
          wordCount: action.payload.split(/\s+/).filter(word => word.length > 0).length,
        },
      };

    case 'SET_THEME':
      return {
        ...state,
        preferences: {
          ...state.preferences,
          theme: action.payload,
        },
      };

    case 'SET_PANEL_SPLIT':
      return {
        ...state,
        preferences: {
          ...state.preferences,
          panelSplit: action.payload,
        },
      };

    case 'TOGGLE_EXPORT_MODAL':
      return {
        ...state,
        exportModal: {
          ...state.exportModal,
          isOpen: !state.exportModal.isOpen,
        },
      };

    case 'SET_FULLSCREEN_MODE':
      return {
        ...state,
        panelState: {
          ...state.panelState,
          fullScreenMode: action.payload,
        },
      };

    case 'UPDATE_DOCUMENT':
      return {
        ...state,
        document: {
          ...state.document,
          ...action.payload,
          lastModified: new Date(),
        },
      };

    case 'UPDATE_PREFERENCES':
      return {
        ...state,
        preferences: {
          ...state.preferences,
          ...action.payload,
        },
      };

    default:
      return state;
  }
};

const getInitialState = (): AppState => {
  // Load preferences from enhanced persistence service
  const preferences = persistenceService.loadPreferences();
  
  // Load saved document or use default
  const savedDocument = persistenceService.loadDocument();
  const document = savedDocument || {
    id: 'default',
    content: '# Welcome to Content Studio\n\nStart writing your markdown here...',
    title: 'Untitled Document',
    lastModified: new Date(),
    wordCount: 8,
  };

  return {
    document,
    preferences,
    panelState: {
      leftWidth: preferences.panelSplit,
      rightWidth: 100 - preferences.panelSplit,
      isResizing: false,
      fullScreenMode: 'none',
    },
    exportModal: {
      isOpen: false,
    },
  };
};

interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider = ({ children }: AppProviderProps) => {
  const [state, dispatch] = useReducer(appReducer, getInitialState());

  // Auto-save preferences when they change
  useEffect(() => {
    persistenceService.savePreferences(state.preferences);
  }, [state.preferences]);

  // Save document changes (but not on every keystroke - that's handled by useAutoSave)
  useEffect(() => {
    // Only save if document has meaningful content and has been modified
    if (state.document.content.trim() && state.document.content !== '# Welcome to Content Studio\n\nStart writing your markdown here...') {
      persistenceService.saveDocument(state.document);
    }
  }, [state.document.id, state.document.title]); // Only save on title/id changes, not content changes

  return (
    <AppContext.Provider value={{ state, dispatch }}>
      {children}
    </AppContext.Provider>
  );
};